import 'braid-design-system/reset';

import { HirerAuthProvider } from '@seek/hirer-auth-react';
import { FeatureToggleProvider } from '@seek/hirer-feature-toggle';
import { HirerApolloProvider } from '@seek/hirer-graphql-react';
import { UivBanner } from '@seek/uiv-banner';
import { VocabProvider } from '@vocab/react';
import { BraidProvider, ToastProvider } from 'braid-design-system';
import seekJobs from 'braid-design-system/themes/seekJobs';
import { useEffect } from 'react';

import { URL_HIRER_GRAPH, URL_HIRER_GRAPH_LOCAL } from 'src/constants/urls';
import { constructCorePageTrackingData } from 'src/modules/AnalyticsTracker/utils/constructPageTrackingData';
import setPageTrackingData from 'src/modules/AnalyticsTracker/utils/setPageTrackingData';
import { useAccountContext } from 'src/modules/ApiClient';
import type { ClientContext } from 'src/types';
import { getAppVersion, getBranch } from 'src/utils/config';

import { MainLayout } from './Layouts/MainLayout/MainLayout';
import DashboardWrapper from './Pages/Dashboard/DashboardWrapper';
import ErrorPage from './Pages/ErrorPage/ErrorPage';

const appName = 'hirer-dashboard';

const AppContents = ({ site, locale, environment }: ClientContext) => {
  const { error } = useAccountContext();

  useEffect(() => {
    setPageTrackingData(constructCorePageTrackingData({ site, locale }));
  }, [locale, site]);

  return (
    <MainLayout site={site} locale={locale}>
      {error ? (
        <ErrorPage site={site} locale={locale} error={error} />
      ) : (
        <DashboardWrapper
          site={site}
          locale={locale}
          environment={environment}
        />
      )}
    </MainLayout>
  );
};

export default ({ site, locale, environment }: ClientContext) => (
  <BraidProvider theme={seekJobs}>
    <ToastProvider>
      <VocabProvider language={locale}>
        <HirerAuthProvider authenticationLevel="Hirer">
          <HirerApolloProvider
            appName={appName}
            site={site}
            uri={
              environment === 'development'
                ? URL_HIRER_GRAPH_LOCAL
                : URL_HIRER_GRAPH
            }
          >
            <FeatureToggleProvider appName={appName} featureConfigs={[]}>
              <UivBanner
                applicationName={appName}
                version={getAppVersion()}
                branch={getBranch()}
              />
              <AppContents
                site={site}
                locale={locale}
                environment={environment}
              />
            </FeatureToggleProvider>
          </HirerApolloProvider>
        </HirerAuthProvider>
      </VocabProvider>
    </ToastProvider>
  </BraidProvider>
);
